import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { BulletV2Panel } from '@/components/bulletGroups/BulletV2Panel';
import { Loader2, Save, ExternalLink, Lock, Undo2, ClipboardCopy, X, Check, RefreshCw, Settings, AlertCircle } from 'lucide-react';

// iter299t — Tier 2 Token palette (mirrors Detailed Multi / Detailed 1
// `INSERT_FIELD_GROUPS` in ScriptAuditPage.js). Keys MUST match the token
// dictionary built server-side by `_build_quotation_tokens` in
// backend/routes/reports.py — any addition/removal must be mirrored there.
const QUOTATION_TOKEN_GROUPS = [
  {
    label: 'Client',
    tokens: [
      { key: 'Client_name',       label: 'Client Name (full)'  },
      { key: 'client_first_name', label: 'First Name'          },
      { key: 'client_last_name',  label: 'Last Name'           },
      { key: 'ethnic_greeting',   label: 'Cultural greeting'   },
      { key: 'Client_email',      label: 'Email'               },
      { key: 'Client_phone',      label: 'Phone (mobile>home)' },
    ],
  },
  {
    label: 'Property',
    tokens: [
      { key: 'Property_Address',  label: 'Full Address' },
      { key: 'Property_Suburb',   label: 'Suburb'       },
      { key: 'Property_State',    label: 'City/Region'  },
      { key: 'Property_Postcode', label: 'Postcode'     },
    ],
  },
  {
    label: 'Roof',
    tokens: [
      { key: 'roof_type',  label: 'Roof Type'  },
      { key: 'roof_pitch', label: 'Roof Pitch' },
      { key: 'roof_area',  label: 'Roof Area'  },
      { key: 'storeys',    label: 'Storeys'    },
    ],
  },
  {
    label: 'Financial',
    tokens: [
      { key: 'quote_total',     label: 'Total incl GST'     },
      { key: 'quote_subtotal',  label: 'Subtotal (ex GST)'  },
      { key: 'gst_amount',      label: 'GST amount'         },
      { key: 'deposit_amount',  label: 'Deposit $'          },
      { key: 'deposit_pct',     label: 'Deposit %'          },
      { key: 'balance_amount',  label: 'Balance on completion' },
      { key: 'actual_materials_cost', label: 'Materials cost (incl GST)' },
    ],
  },
  {
    label: 'Company',
    tokens: [
      { key: 'Company_Bank_Name',      label: 'Bank name'      },
      { key: 'Company_Account_Number', label: 'Account number' },
      { key: 'Company_Account_Holder', label: 'Account holder' },
    ],
  },
  {
    label: 'Terms / Dates',
    tokens: [
      { key: 'quote_date',          label: 'Quote Date'         },
      { key: 'quote_valid_until',   label: 'Valid Until'        },
      { key: 'quote_valid_days',    label: 'Valid (days)'       },
      { key: 'commencement_window', label: 'Commencement window'},
      { key: 'quote_ref',           label: 'Quote Ref (A####)'  },
      { key: 'representative_name', label: 'Representative'     },
    ],
  },
];

/**
 * QuotationLetterEditor · iter298s
 *
 * Renders the Quotation Letter (`/reports/quotation-detailed`) as a
 * stacked list of 9 sections in the exact PDF order. 6 fields are
 * inline-editable with:
 *   • 900ms debounced auto-save
 *   • Per-field undo history (ring buffer, 12 states) + Ctrl+Z shortcut
 *   • Copy-from-another-quote-type modal picker (mirrors
 *     AreaDefectsAccordion's Detailed Multi Copy-From UX)
 *
 * Quote-type-scoped editing:
 *   • On mount, if no quote_type is chosen the operator sees a mandatory
 *     picker prompt — nothing else renders until they pick one. Selection
 *     is persisted to localStorage so switching Studio tabs and back
 *     doesn't re-prompt.
 *   • The PDF preview iframe (left column) stays on refAppraisalId; the
 *     editor pane loads THAT quote_type's fields from `quote_types`
 *     independent of the appraisal.
 *
 * Split Terms (previously a single read-only bulleted list) now renders
 * as 5 sub-rows with Deposit/Commencement/Validity editable via a small
 * PATCH to `/appraisals/{id}/conditions` (new fields whitelisted server-
 * side in iter298s).
 */

// Stage 2 · bullet wording per builder variant (mirrors routes/bullet_rules.py bullet_variant_for).
function bulletVariant(qt) {
  const so = (qt?.service_option || '').trim();
  if (so === 'Re-Roof') return 'reroof';
  if (so === 'Guttering') return 'guttering';
  if (so === 'Maintenance') return 'maintenance';
  if (so === 'Re-Coat') return 'recoat';
  if (so === 'Repairs') {
    const rts = (qt?.roof_types || []).filter(r => (r || '').toLowerCase() !== 'all').join(' ').toLowerCase();
    return /iron|tin|longrun/.test(rts) ? 'repairs_iron' : (rts ? 'repairs_tile' : 'repairs_iron');
  }
  return '';
}
function variantLabel(rule, variant) {
  return (rule.labels_by_variant || {})[variant] || rule.default_label;
}
function BulletWordingInput({ api, rule, variant, onSaved }) {
  const current = variantLabel(rule, variant);
  const [val, setVal] = useState(current);
  useEffect(() => { setVal(current); }, [current]);
  const save = async () => {
    if (val.trim() === current) return;
    try {
      const patch = variant
        ? { labels_by_variant: { ...(rule.labels_by_variant || {}), [variant]: val.trim() } }
        : { default_label: val.trim() };
      await api.put(`/bullet-rules/${rule._id}`, patch);
      toast.success(`Bullet wording saved${variant ? ` for ${variant}` : ''}`);
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  return (
    <input value={val} onChange={(e) => setVal(e.target.value)} onBlur={save}
      onClick={(e) => e.preventDefault()}
      className="w-full font-semibold text-slate-800 bg-transparent border-b border-dashed border-slate-300 focus:border-emerald-500 outline-none"
      data-testid={`quotation-bullet-wording-${rule.key}`} />
  );
}

export default function QuotationLetterEditor({ api, refAppraisalId, onSaved, variant = 'quotation_letter' }) {
  // iter299y — Variant-aware editor. When `variant === 'invoice_letter'`
  // the same UI, plumbing, and sub-components are reused but the draft
  // slots + DB fields switch to the `inv_*` / `invoice_*` prefixes so
  // an operator's invoice edits never spill into the quotation cells
  // (and vice versa). Separate localStorage key per variant so the
  // selected quote_type is remembered independently.
  const isInvoice = variant === 'invoice_letter';
  const LS_KEY = isInvoice ? 'ql_editor_selected_quote_type_invoice' : 'ql_editor_selected_quote_type';
  const [selectedQtRef, setSelectedQtRef] = useState(() => {
    try { return localStorage.getItem(LS_KEY) || ''; } catch { return ''; }
  });  const [quoteTypes, setQuoteTypes] = useState([]);  // full list of 34 quote_types
  const [appraisal, setAppraisal] = useState(null);
  const [quoteType, setQuoteType] = useState(null);   // selected quote_types doc
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState({});
  const [savedAt, setSavedAt] = useState({});
  // iter303d — Per-field save state for the resilient auto-save.
  //   { [key]: { attempt: 1|2|3, phase: 'idle'|'saving'|'retry'|'failed', lastError?: string } }
  // Populated by saveField during retry loop. Consumed by the SaveStatePill
  // component (rendered by TitleBodyCard / EditCard).
  const [saveState, setSaveState] = useState({});
  const [pickerOpen, setPickerOpen] = useState(!selectedQtRef);
  const [copyFromField, setCopyFromField] = useState(null); // 'qt_description' | 'qt_details'

  const [drafts, setDrafts] = useState({
    appraisal_condition: '',
    qt_description: '',
    qt_details: '',
    // Iter317 · Batch Q (2026-02 · A999421 · Bob) — Bullet Summary
    // template for section 6 "Repairs Detailed as Follows:" auto-
    // populated from `component_lines` at PDF render. Empty falls
    // back to `appraisal_condition` free text.
    qt_bullet_summary: '',
    // v10.29 Sprint 4c-follow (2026-02) · Auto-Bullet Rules & Allowlist.
    // Layer 2 fields per quote_type — replaces the legacy free-text
    // template above with a checkbox-driven allowlist of component_types
    // and a verbatim extras list.
    qt_bullet_include: [],
    qt_bullet_extra_lines: [],
    deposit_pct: '',
    quote_commencement: '',
    quote_valid_days: '',
    // iter298t — 14 new per-quote-type Quotation Letter title/body overrides.
    // Each pair backs one previously-hardcoded cell in the PDF. Empty
    // strings mean "use the app_settings.quotation_defaults fallback".
    qt_thanks_title: '', qt_thanks_body: '',
    qt_payment_title: '', qt_payment_body: '',
    qt_deposit_title: '', qt_deposit_body: '',
    qt_commencement_title: '', qt_commencement_body: '',
    qt_general_title: '', qt_general_body: '',
    qt_validity_title: '', qt_validity_body: '',
    qt_representative_title: '', qt_representative_body: '',
    // iter299y — Invoice Letter variant slots. Prefix `inv_*` keeps them
    // isolated from the quotation drafts so switching variants is safe.
    inv_thanks_title: '', inv_thanks_body: '',
    inv_description: '', inv_details: '',
    inv_payment_terms_title: '', inv_payment_terms_body: '',
    inv_payment_amount_title: '', inv_payment_amount_body: '',
    inv_representative_title: '', inv_representative_body: '',
  });
  // Undo ring-buffers per editable field. Newest values at the END of the
  // array; pop() to undo, then push the CURRENT value back on redo (redo
  // deliberately out of scope this iteration — user only asked for undo).
  const [undoStacks, setUndoStacks] = useState({});
  const debounceRefs = useRef({});
  // iter299t — Token insertion at caret. `lastFocusedRef.current` holds
  // the {el, fieldKey} of the most-recently-focused editable field so a
  // Token Palette chip click knows where to splice `{key}`. The field
  // registers itself via the `data-field-key` HTML attribute + an
  // onFocus handler wired below.
  const lastFocusedRef = useRef(null);

  // ─── Data loading ─────────────────────────────────────────────────────
  // v10.29 Sprint 4c-follow (2026-02) · Auto-Bullet Rules — Layer 1
  // catalogue loaded once. Feeds the checkbox editor in step 6a.
  const [bulletRules, setBulletRules] = useState([]);
  const loadBulletRules = useCallback(async () => {
    try {
      const { data } = await api.get('/bullet-rules');
      setBulletRules(Array.isArray(data) ? data : []);
    } catch (e) {
      // Non-fatal — the section falls back to legacy free-text editor
      // when rules are unavailable.
      console.warn('bullet-rules load failed:', e?.message);
    }
  }, [api]);

  const loadQuoteTypes = useCallback(async () => {
    try {
      const { data } = await api.get('/quote-types');
      setQuoteTypes(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }, [api]);
  const loadAppraisal = useCallback(async () => {
    if (!refAppraisalId) return;
    try {
      const { data } = await api.get(`/appraisals/${refAppraisalId}`);
      setAppraisal(data);
      setDrafts(d => ({
        ...d,
        appraisal_condition: data.appraisal_condition || '',
        deposit_pct: data.deposit_pct ?? '',
        quote_valid_days: data.quote_valid_days ?? '',
      }));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }, [api, refAppraisalId]);
  const loadSelectedQuoteType = useCallback(async () => {
    if (!selectedQtRef) { setQuoteType(null); return; }
    try {
      const { data } = await api.get(`/quote-types/${encodeURIComponent(selectedQtRef)}`);
      setQuoteType(data);
      setDrafts(d => ({
        ...d,
        qt_description: data?.description || '',
        qt_details: data?.details || '',
        qt_bullet_summary: data?.bullet_summary || '',
        qt_bullet_include: Array.isArray(data?.bullet_include) ? data.bullet_include : [],
        qt_bullet_extra_lines: Array.isArray(data?.bullet_extra_lines) ? data.bullet_extra_lines : [],
        qt_bullet_engine: data?.bullet_engine || '',
        qt_bullet_v2_groups: Array.isArray(data?.bullet_v2_groups) ? data.bullet_v2_groups : null,
        qt_thanks_title: data?.thanks_title || '',
        qt_thanks_body: data?.thanks_body || '',
        qt_payment_title: data?.payment_title || '',
        qt_payment_body: data?.payment_body || '',
        qt_deposit_title: data?.deposit_title || '',
        qt_deposit_body: data?.deposit_body || '',
        qt_commencement_title: data?.commencement_title || '',
        qt_commencement_body: data?.commencement_body || '',
        qt_general_title: data?.general_title || '',
        qt_general_body: data?.general_body || '',
        qt_validity_title: data?.validity_title || '',
        qt_validity_body: data?.validity_body || '',
        qt_representative_title: data?.representative_title || '',
        qt_representative_body: data?.representative_body || '',
        // Commencement window is per quote type (2026-06).
        quote_commencement: data?.quote_commencement || '',
        // Deposit = greater of standard % of quote or supplier buy cost + uplift % (per quote type).
        default_deposit_pct: data?.default_deposit_pct ?? '',
        buy_uplift_pct: data?.buy_uplift_pct ?? '',
        // iter299y — Also load parallel Invoice Letter fields so
        // switching between the Quotation Letter and Invoice Letter
        // tabs does not require a refetch.
        inv_thanks_title: data?.invoice_thanks_title || '',
        inv_thanks_body: data?.invoice_thanks_body || '',
        inv_description: data?.invoice_description || '',
        inv_details: data?.invoice_details || '',
        inv_payment_terms_title: data?.invoice_payment_terms_title || '',
        inv_payment_terms_body: data?.invoice_payment_terms_body || '',
        inv_payment_amount_title: data?.invoice_payment_amount_title || '',
        inv_payment_amount_body: data?.invoice_payment_amount_body || '',
        inv_representative_title: data?.invoice_representative_title || '',
        inv_representative_body: data?.invoice_representative_body || '',
      }));
      // Reset undo stacks for the quote-type-scoped fields when switching
      // types — a Ctrl+Z on "Notes:" for "Rescrew and Repair Longrun"
      // should NOT restore text from another quote_type.
      setUndoStacks(s => ({ ...s, qt_description: [], qt_details: [], inv_description: [], inv_details: [] }));
    } catch (e) {
      setQuoteType(null);
    }
  }, [api, selectedQtRef]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadQuoteTypes(), loadAppraisal(), loadBulletRules()]);
      setLoading(false);
    })();
  }, [loadQuoteTypes, loadAppraisal, loadBulletRules]);
  useEffect(() => { loadSelectedQuoteType(); }, [loadSelectedQuoteType]);

  // iter303d — beforeunload guard. Warns the operator before they
  // navigate away or reload if there are unsaved changes OR pending
  // retries. Uses the standard preventDefault + returnValue pattern
  // that all modern browsers surface as a native confirmation dialog.
  useEffect(() => {
    const handler = (e) => {
      const hasDirty = Object.keys(dirty).length > 0;
      const hasFailed = Object.values(saveState).some(s => s?.phase === 'failed');
      if (hasDirty || hasFailed) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty, saveState]);

  // ─── Save helpers ─────────────────────────────────────────────────────
  // iter303d — Resilient save with 3-attempt retry (exponential backoff:
  // 500 ms → 1500 ms → 4000 ms). Cloudflare 520s and transient network
  // errors during backend restarts previously killed the save with a
  // banner error and no retry — see the "Restore ColorTile" incident.
  // Now, if the request fails with a network error or a 5xx, we back off
  // and re-attempt. Only after all 3 attempts fail does the banner
  // surface. Dirty state is preserved on failure so the value stays in
  // the editor and can be re-triggered by another keystroke or a manual
  // retry (the SaveStatePill shows a click-to-retry when phase='failed').
  // The function also RETURNS a promise so flush-on-switch can await it.
  const isRetryableError = (err) => {
    if (!err) return false;
    if (err.code === 'ECONNABORTED') return true; // timeout
    if (err.message && /Network Error|timeout/i.test(err.message)) return true;
    const status = err?.response?.status;
    if (status && status >= 500 && status <= 599) return true;
    if (status === 502 || status === 503 || status === 504 || status === 520) return true;
    return false;
  };

  const attemptOnce = useCallback(async (key, value) => {
    if (key === 'quote_commencement') {
      if (!quoteType || !quoteType.ref) return { skipStateSync: true };
      await api.put(`/quote-types/${encodeURIComponent(quoteType.ref)}`, { quote_commencement: value });
      return { beField: 'quote_commencement', value };
    }
    if (key === 'default_deposit_pct' || key === 'buy_uplift_pct') {
      if (!quoteType || !quoteType.ref) return { skipStateSync: true };
      await api.put(`/quote-types/${encodeURIComponent(quoteType.ref)}`, { [key]: value === '' ? null : Number(value) });
      return { beField: key, value };
    }
    if (['appraisal_condition', 'deposit_pct', 'quote_valid_days'].includes(key)) {
      let payload = { [key]: value };
      if (key === 'deposit_pct') payload = { deposit_pct: value === '' ? '' : Number(value) };
      if (key === 'quote_valid_days') payload = { quote_valid_days: value === '' ? '' : parseInt(value, 10) };
      await api.patch(`/appraisals/${refAppraisalId}/conditions`, payload);
      return { skipStateSync: true };
    }
    if (key === 'qt_description' || key === 'qt_details') {
      if (!quoteType || !quoteType.ref) return { skipStateSync: true };
      const beField = key === 'qt_description' ? 'description' : 'details';
      const patch = { ...quoteType, [beField]: value };
      await api.put(`/quote-types/${encodeURIComponent(quoteType.ref)}`, patch);
      return { beField, value };
    }
    if (key.startsWith('qt_')) {
      if (!quoteType || !quoteType.ref) return { skipStateSync: true };
      const beField = key.slice(3);
      const patch = { ...quoteType, [beField]: value };
      await api.put(`/quote-types/${encodeURIComponent(quoteType.ref)}`, patch);
      return { beField, value };
    }
    if (key.startsWith('inv_')) {
      if (!quoteType || !quoteType.ref) return { skipStateSync: true };
      const beField = 'invoice_' + key.slice(4);
      const patch = { ...quoteType, [beField]: value };
      await api.put(`/quote-types/${encodeURIComponent(quoteType.ref)}`, patch);
      return { beField, value };
    }
    return { skipStateSync: true };
  }, [api, refAppraisalId, quoteType]);

  const saveField = useCallback(async (key, value) => {
    const MAX_ATTEMPTS = 3;
    const BACKOFFS = [500, 1500, 4000];
    let lastErr = null;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      setSaveState(s => ({ ...s, [key]: { attempt, phase: 'saving' } }));
      try {
        const res = await attemptOnce(key, value);
        // Success — mirror into React state so the editor + Copy-From
        // library see the write immediately.
        if (res && !res.skipStateSync && quoteType?.ref) {
          const { beField } = res;
          setQuoteType(qt => ({ ...qt, [beField]: value }));
          setQuoteTypes(list => list.map(qt =>
            qt.ref === quoteType.ref ? { ...qt, [beField]: value } : qt
          ));
        }
        setSavedAt(p => ({ ...p, [key]: Date.now() }));
        setDirty(p => { const n = { ...p }; delete n[key]; return n; });
        setSaveState(s => { const n = { ...s }; delete n[key]; return n; });
        onSaved?.();
        return true;
      } catch (e) {
        lastErr = e;
        if (attempt < MAX_ATTEMPTS && isRetryableError(e)) {
          setSaveState(s => ({ ...s, [key]: { attempt, phase: 'retry', lastError: e?.message || 'transient error' } }));
          await new Promise(r => setTimeout(r, BACKOFFS[attempt - 1]));
          continue;
        }
        break;
      }
    }
    // All attempts exhausted — keep dirty=true so the value stays in the
    // draft and the SaveStatePill offers a manual retry.
    setSaveState(s => ({
      ...s,
      [key]: { attempt: MAX_ATTEMPTS, phase: 'failed', lastError: lastErr?.response?.data?.detail || lastErr?.message || 'save failed' }
    }));
    setError(`Save failed on ${key} after ${MAX_ATTEMPTS} attempts: ${lastErr?.response?.data?.detail || lastErr?.message || 'unknown error'}. Value preserved — click the "retry" pill on the field to try again.`);
    return false;
  }, [attemptOnce, quoteType, onSaved]);

  // iter303d — Manual retry from the SaveStatePill (phase='failed'). We
  // simply re-invoke saveField with the current draft value.
  const retrySaveField = useCallback((key) => {
    // Pull the fresh current value from drafts state via functional set.
    setDrafts(d => { saveField(key, d[key] ?? ''); return d; });
  }, [saveField]);

  const setField = (key, value) => {
    // Push CURRENT value onto the undo stack BEFORE mutating draft. This
    // ensures the stack always reflects "what was here before the last
    // keystroke" — one Ctrl+Z restores that. Cap at 12 entries so
    // long-form editing doesn't leak memory.
    setUndoStacks(s => {
      const prev = s[key] || [];
      const current = drafts[key] ?? '';
      // Skip pushing if the last entry equals current (avoids stack
      // filling with identical values during fast repeated keystrokes).
      if (prev[prev.length - 1] === current) return s;
      const next = [...prev, current].slice(-12);
      return { ...s, [key]: next };
    });
    setDrafts(d => ({ ...d, [key]: value }));
    setDirty(p => ({ ...p, [key]: true }));
    if (debounceRefs.current[key]) clearTimeout(debounceRefs.current[key]);
    debounceRefs.current[key] = setTimeout(() => saveField(key, value), 900);
  };

  const undoField = (key) => {
    const stack = undoStacks[key] || [];
    if (stack.length === 0) return;
    const prev = stack[stack.length - 1];
    // Cancel any pending debounced save so the undo doesn't get
    // overwritten by the previous value re-saving after 900ms.
    if (debounceRefs.current[key]) clearTimeout(debounceRefs.current[key]);
    setUndoStacks(s => ({ ...s, [key]: stack.slice(0, -1) }));
    setDrafts(d => ({ ...d, [key]: prev }));
    // Fire immediate save so the DB matches what the user sees post-undo.
    saveField(key, prev);
  };

  // iter299t — Focus tracker + token inserter for the Token Palette.
  // Every editable input/textarea calls `onFieldFocus` on focus which
  // records the element + fieldKey. `insertToken` reads the current
  // caret position from that element, splices `{key}` at the caret,
  // pushes the mutated value through the standard setField path (so
  // undo history + debounced save both fire), then re-focuses & restores
  // the caret one character past the closing brace.
  const onFieldFocus = useCallback((e) => {
    const el = e.currentTarget;
    const fieldKey = el.dataset.fieldKey;
    if (!fieldKey) return;
    lastFocusedRef.current = { el, fieldKey };
  }, []);

  const insertToken = useCallback((tokenKey) => {
    const focus = lastFocusedRef.current;
    if (!focus) {
      setError('Click into a text field first, then click a token chip to insert it.');
      return;
    }
    const { el, fieldKey } = focus;
    // Support both textarea + input — both expose selectionStart/End.
    const start = el.selectionStart ?? (el.value || '').length;
    const end = el.selectionEnd ?? start;
    const currentValue = drafts[fieldKey] ?? '';
    const insertText = `{${tokenKey}}`;
    const nextValue = currentValue.slice(0, start) + insertText + currentValue.slice(end);
    setField(fieldKey, nextValue);
    // Restore focus + caret AFTER React commits the new value.
    requestAnimationFrame(() => {
      try {
        el.focus();
        const caret = start + insertText.length;
        el.setSelectionRange(caret, caret);
      } catch { /* element unmounted or non-focusable */ }
    });
  }, [drafts]);

  // ─── Copy-From helpers ────────────────────────────────────────────────
  // iter299u — Copy From now supports two modes:
  //   1. "single" — cell 4 (Description) + cell 7 (Notes). Existing
  //      behaviour: copies ONE field from a source quote_type.
  //   2. "pair"   — cells 2, 8a-8e, 9. Copies BOTH title AND body
  //      together in one action (matches how these cells are edited).
  //
  // TITLE_BODY_COPY_MAP is keyed by the modal-open sentinel string; each
  // entry describes the draft keys we write on the current quote_type
  // AND the DB keys we READ from the source quote_type doc.
  const TITLE_BODY_COPY_MAP = {
    qt_thanks_pair:         { label: 'Thanks (Cell 2)',           titleDraft: 'qt_thanks_title',         titleDb: 'thanks_title',         bodyDraft: 'qt_thanks_body',         bodyDb: 'thanks_body'         },
    qt_payment_pair:        { label: 'Payment Terms (Cell 8a)',   titleDraft: 'qt_payment_title',        titleDb: 'payment_title',        bodyDraft: 'qt_payment_body',        bodyDb: 'payment_body'        },
    qt_deposit_pair:        { label: 'Deposit (Cell 8b)',         titleDraft: 'qt_deposit_title',        titleDb: 'deposit_title',        bodyDraft: 'qt_deposit_body',        bodyDb: 'deposit_body'        },
    qt_commencement_pair:   { label: 'Commencement (Cell 8c)',    titleDraft: 'qt_commencement_title',   titleDb: 'commencement_title',   bodyDraft: 'qt_commencement_body',   bodyDb: 'commencement_body'   },
    qt_general_pair:        { label: 'General Condition (Cell 8d)', titleDraft: 'qt_general_title',      titleDb: 'general_title',        bodyDraft: 'qt_general_body',        bodyDb: 'general_body'        },
    qt_validity_pair:       { label: 'Validity (Cell 8e)',        titleDraft: 'qt_validity_title',       titleDb: 'validity_title',       bodyDraft: 'qt_validity_body',       bodyDb: 'validity_body'       },
    qt_representative_pair: { label: 'Representative (Cell 9)',   titleDraft: 'qt_representative_title', titleDb: 'representative_title', bodyDraft: 'qt_representative_body', bodyDb: 'representative_body' },
    // iter299y — Invoice Letter pair-copy entries.
    inv_thanks_pair:          { label: 'Invoice Thanks (Cell 2)',          titleDraft: 'inv_thanks_title',          titleDb: 'invoice_thanks_title',          bodyDraft: 'inv_thanks_body',          bodyDb: 'invoice_thanks_body'          },
    inv_payment_terms_pair:   { label: 'Invoice Payment Terms (Cell 8a)',  titleDraft: 'inv_payment_terms_title',   titleDb: 'invoice_payment_terms_title',   bodyDraft: 'inv_payment_terms_body',   bodyDb: 'invoice_payment_terms_body'   },
    inv_payment_amount_pair:  { label: 'Invoice Payment Amount (Cell 8b)', titleDraft: 'inv_payment_amount_title',  titleDb: 'invoice_payment_amount_title',  bodyDraft: 'inv_payment_amount_body',  bodyDb: 'invoice_payment_amount_body'  },
    inv_representative_pair:  { label: 'Invoice Representative (Cell 9)',  titleDraft: 'inv_representative_title',  titleDb: 'invoice_representative_title',  bodyDraft: 'inv_representative_body',   bodyDb: 'invoice_representative_body'   },
  };

  const doCopyFrom = (sourceQt, field) => {
    const pair = TITLE_BODY_COPY_MAP[field];
    if (pair) {
      // Copy title + body together. setField() covers undo + auto-save.
      setField(pair.titleDraft, sourceQt[pair.titleDb] || '');
      setField(pair.bodyDraft, sourceQt[pair.bodyDb] || '');
    } else if (field === 'inv_description' || field === 'inv_details') {
      // iter299y — Invoice single-field copies (cells 4 & 7 on invoice).
      const beField = field === 'inv_description' ? 'invoice_description' : 'invoice_details';
      setField(field, sourceQt[beField] || '');
    } else {
      // Single-field legacy path — Quotation cells 4 (description) + 7 (details).
      const value = field === 'qt_description' ? (sourceQt.description || '') : (sourceQt.details || '');
      setField(field, value);
    }
    setCopyFromField(null);
  };

  // iter299u — Body-only library options for the 7 TitleBodyCards.
  // Returns [{qtRef, value}] for every OTHER quote_type that has a
  // non-empty value on the given DB body key. Used by the inline
  // "Load body from library" <select> on each cell body field.
  const bodyLibraryOptions = (dbBodyKey) => {
    return quoteTypes
      .filter(qt => qt.ref !== selectedQtRef && (qt[dbBodyKey] || '').trim())
      .map(qt => ({ qtRef: qt.ref, value: (qt[dbBodyKey] || '').trim() }));
  };

  // ─── Quote-type selector ──────────────────────────────────────────────
  const chooseQuoteType = async (ref) => {
    // iter303d — Flush any pending debounced saves BEFORE switching so
    // in-flight text on the current variant is guaranteed to be
    // persisted (with retry). Prevents the "typed content lost when I
    // clicked a new variant too fast" class of bug.
    const pendingKeys = Object.keys(debounceRefs.current || {});
    if (pendingKeys.length) {
      const flushPromises = [];
      for (const k of pendingKeys) {
        clearTimeout(debounceRefs.current[k]);
        delete debounceRefs.current[k];
        // Use the CURRENT draft value (functional read of drafts state).
        flushPromises.push(new Promise(resolve => {
          setDrafts(d => {
            saveField(k, d[k] ?? '').then(resolve, resolve);
            return d;
          });
        }));
      }
      // Wait for all flushes to settle before loading the next variant,
      // otherwise the newly-fetched quote_type doc could get overwritten
      // by a stale in-flight PUT.
      try { await Promise.allSettled(flushPromises); } catch { /* best-effort */ }
    }
    setSelectedQtRef(ref);
    try { localStorage.setItem(LS_KEY, ref); } catch { /* localStorage may be blocked */ }
    setPickerOpen(false);
    // iter299v — Refetch the quote_types list on every switch so the
    // Copy-From modal + Library dropdown reflect any edits made in
    // another browser tab / session since the initial mount.
    loadQuoteTypes();
  };

  // ─── Guards / render ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="col-span-12 lg:col-span-7 bg-white border border-[#E2E2DF] rounded-md p-6 flex items-center gap-2 text-slate-500 text-sm">
        <Loader2 className="animate-spin" size={14} /> Loading Quotation Letter data…
      </div>
    );
  }
  if (pickerOpen) {
    return (
      <div className="col-span-12 lg:col-span-7 bg-white border border-emerald-300 rounded-md p-6">
        <QuoteTypePicker
          list={quoteTypes}
          onPick={chooseQuoteType}
          currentRef={selectedQtRef}
        />
      </div>
    );
  }

  const clientName = appraisal ? `${appraisal.client?.name_first || ''} ${appraisal.client?.name_last || ''}`.trim() : '';
  const address = appraisal ? [
    `${appraisal.property?.property_address_num || ''} ${appraisal.property?.property_address_street || ''}`.trim(),
    appraisal.property?.property_address_suburb || '',
    appraisal.property?.property_address_city || '',
  ].filter(Boolean).join(', ') : '';
  const tok = typeof window !== 'undefined' ? (localStorage.getItem('access_token') || '') : '';

  return (
    <div className="col-span-12 lg:col-span-7 bg-white border border-[#E2E2DF] rounded-md overflow-y-auto" data-testid="quotation-letter-editor">
      {/* Header — quote_type context strip */}
      <div className="sticky top-0 bg-gradient-to-r from-emerald-50 to-white border-b border-emerald-200 px-4 py-3 z-10">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="min-w-0">
            <div className="flex items-baseline gap-2">
              <h3 className="text-sm font-bold text-emerald-900">📝 Quotation Letter</h3>
              <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-800/70">Editing:</span>
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                data-testid="quotation-change-quote-type"
                className="text-xs font-bold text-emerald-900 border border-emerald-400 px-2 py-0.5 rounded hover:bg-emerald-100"
              >
                {selectedQtRef || '(none)'} ▾
              </button>
            </div>
            <p className="text-[10px] text-emerald-800/60 mt-1">
              Preview stays on the current test appraisal · Ctrl+Z or ↺ button per field to undo · auto-saves at 900ms.
            </p>
          </div>
          <div className="flex items-center gap-1">
            <a href={`/quote-types-manage`} className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold border border-slate-300 text-slate-700 rounded hover:bg-slate-50" title="Open the full Quote Types admin page">
              <Settings size={11} /> Manage Quote Types
            </a>
            <a
              data-testid="quotation-letter-open-pdf"
              href={`${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/reports/quotation-detailed/${refAppraisalId}?_bust=${Date.now()}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold border border-emerald-300 text-emerald-800 rounded hover:bg-emerald-50"
            >
              <ExternalLink size={11} /> Open PDF
            </a>
          </div>
        </div>
        {error && (
          <div className="mt-2 px-2 py-1 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError('')}><X size={10} /></button>
          </div>
        )}
        {/* iter303d — Live save-state strip. Aggregates the per-field
            saveState + dirty maps into ONE always-visible indicator so
            operators never have to guess whether their typing has been
            persisted after the 900ms debounce. Three modes:
              • All saved — green "All changes saved" pill (default)
              • Saving/retrying — amber "Saving N field(s)…" with retry counter
              • Failed  — red "N save(s) failed" + one-click Retry-All button
        */}
        {(() => {
          const savingKeys = Object.keys(saveState).filter(k => saveState[k]?.phase === 'saving' || saveState[k]?.phase === 'retry');
          const failedKeys = Object.keys(saveState).filter(k => saveState[k]?.phase === 'failed');
          const dirtyCount = Object.keys(dirty).length;
          if (failedKeys.length) {
            return (
              <div data-testid="save-state-strip-failed" className="mt-2 px-2.5 py-1.5 text-[11px] text-red-800 bg-red-50 border border-red-300 rounded flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 font-semibold">
                  <AlertCircle size={12} /> {failedKeys.length} save{failedKeys.length > 1 ? 's' : ''} failed ({failedKeys.slice(0, 2).join(', ')}{failedKeys.length > 2 ? '…' : ''})
                </span>
                <button
                  data-testid="retry-failed-saves-btn"
                  onClick={() => failedKeys.forEach(k => retrySaveField(k))}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white bg-red-600 rounded hover:bg-red-700"
                >
                  <RefreshCw size={10} /> Retry all
                </button>
              </div>
            );
          }
          if (savingKeys.length) {
            const primary = saveState[savingKeys[0]];
            const suffix = primary?.attempt > 1 ? ` — retry ${primary.attempt}/3` : '';
            return (
              <div data-testid="save-state-strip-saving" className="mt-2 px-2.5 py-1 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded flex items-center gap-1.5">
                <Loader2 size={11} className="animate-spin" />
                <span>Saving {savingKeys.length} field{savingKeys.length > 1 ? 's' : ''}{suffix}…</span>
              </div>
            );
          }
          if (dirtyCount > 0) {
            return (
              <div data-testid="save-state-strip-dirty" className="mt-2 px-2.5 py-1 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded flex items-center gap-1.5">
                <Loader2 size={11} className="animate-spin opacity-40" />
                <span>{dirtyCount} unsaved change{dirtyCount > 1 ? 's' : ''} (auto-saving)</span>
              </div>
            );
          }
          return (
            <div data-testid="save-state-strip-clean" className="mt-2 px-2.5 py-1 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded flex items-center gap-1.5">
              <Check size={11} /> All changes saved
            </div>
          );
        })()}
      </div>

      <div className="p-4 space-y-3">
        {/* 1️⃣ Client & Property IDs */}
        <ReadOnlyCard step="1" title="Client & Property IDs">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
            <dt className="text-slate-500">Client:</dt><dd className="text-slate-800">{clientName || '—'}</dd>
            <dt className="text-slate-500">Address:</dt><dd className="text-slate-800">{address || '—'}</dd>
            <dt className="text-slate-500">Client ID:</dt><dd className="text-slate-800 font-mono">C{appraisal?.client?.client_no || '—'}</dd>
            <dt className="text-slate-500">Property ID:</dt><dd className="text-slate-800 font-mono">P{appraisal?.property?.property_no || '—'}</dd>
            <dt className="text-slate-500">Appraisal ID:</dt><dd className="text-slate-800 font-mono">A{appraisal?.appraisal_no || '—'}</dd>
          </dl>
        </ReadOnlyCard>
        {/* iter299t — Token Palette. One persistent instance below the
            Client / Property IDs card. Click any text field first,
            then a chip to splice {token} at the caret. */}
        <TokenPalette onInsert={insertToken} />

        {/* iter299y — Variant-aware cell rendering. Quotation Letter
            renders 9 cells including 5 term blocks + acceptance box.
            Invoice Letter renders the same layout with invoice wording
            + only 2 term blocks (Payment Terms + Payment Amount) + no
            signature/acceptance box. */}
        {isInvoice ? (<>
        {/* 2️⃣ Thanks — Invoice */}
        <TitleBodyCard
          step="2"
          testId="invoice-thanks"
          titleFieldKey="inv_thanks_title" bodyFieldKey="inv_thanks_body" onFieldFocus={onFieldFocus}
          onCopyFrom={() => setCopyFromField('inv_thanks_pair')}
          bodyLibraryOptions={bodyLibraryOptions('invoice_thanks_body')}
          titleValue={drafts.inv_thanks_title}
          bodyValue={drafts.inv_thanks_body}
          titleDefault={'Thank you'}
          bodyDefault={`Thank you for your business. This invoice details the services provided for the remedial repair of your roof at: ${address || '{Property_Address}'}`}
          onTitleChange={v => setField('inv_thanks_title', v)}
          onBodyChange={v => setField('inv_thanks_body', v)}
          onUndoTitle={() => undoField('inv_thanks_title')}
          onUndoBody={() => undoField('inv_thanks_body')}
          titleUndoAvail={(undoStacks.inv_thanks_title || []).length > 0}
          bodyUndoAvail={(undoStacks.inv_thanks_body || []).length > 0}
          titleDirty={dirty.inv_thanks_title} titleSavedAt={savedAt.inv_thanks_title}
          bodyDirty={dirty.inv_thanks_body} bodySavedAt={savedAt.inv_thanks_body}
          hint={`Per quote_type override (invoice_thanks_body). Empty = PDF prints the hardcoded invoice default.`}
        />
        {/* 3️⃣ */}
        <ReadOnlyCard step="3" title="Invoice Type" hint="Sourced from the appraisal's quote_type — same as the Quotation Letter.">
          <div className="text-[12px] text-slate-800 font-semibold">{selectedQtRef || '—'}</div>
        </ReadOnlyCard>
        {/* 4️⃣ Includes — Invoice */}
        <EditCard
          step="4" title='"Our Invoice Includes the Following:"'
          hint={`quote_types.invoice_description for "${selectedQtRef}". One bullet per line. Left blank falls back to the Quotation description.`}
          testId="invoice-includes"
          fieldKey="inv_description" onFieldFocus={onFieldFocus}
          value={drafts.inv_description}
          onChange={v => setField('inv_description', v)}
          onUndo={() => undoField('inv_description')}
          undoAvailable={(undoStacks.inv_description || []).length > 0}
          onCopyFrom={() => setCopyFromField('inv_description')}
          dirty={dirty.inv_description} savedAt={savedAt.inv_description}
          rows={6}
        />
        {/* 5️⃣ */}
        <ReadOnlyCard step="5" title="Price Block + Extras Breakdown" hint="Auto-computed. Extras render above the total when appraisal.extras[] has ≥1 line item." />
        {/* 6️⃣ Repairs — same appraisal_condition field as Quotation Letter */}
        <EditCard
          step="6" title='"Repairs Detailed as Follows:"'
          hint="Same field as the Roof Appraisal Cover Narrative — edits here save to the appraisal and update both PDFs."
          testId="invoice-repairs"
          fieldKey="appraisal_condition" onFieldFocus={onFieldFocus}
          value={drafts.appraisal_condition}
          onChange={v => setField('appraisal_condition', v)}
          onUndo={() => undoField('appraisal_condition')}
          undoAvailable={(undoStacks.appraisal_condition || []).length > 0}
          dirty={dirty.appraisal_condition} savedAt={savedAt.appraisal_condition}
          rows={6}
        />
        {/* 7️⃣ Notes — Invoice */}
        <EditCard
          step="7" title='"Notes:"'
          hint={`quote_types.invoice_details for "${selectedQtRef}". One note per line. Left blank falls back to the Quotation details.`}
          testId="invoice-notes"
          fieldKey="inv_details" onFieldFocus={onFieldFocus}
          value={drafts.inv_details}
          onChange={v => setField('inv_details', v)}
          onUndo={() => undoField('inv_details')}
          undoAvailable={(undoStacks.inv_details || []).length > 0}
          onCopyFrom={() => setCopyFromField('inv_details')}
          dirty={dirty.inv_details} savedAt={savedAt.inv_details}
          rows={5}
        />
        {/* 8️⃣ TERMS — INVOICE has only 2 sub-cells (Payment Terms + Payment Amount) */}
        <div className="border border-orange-300 rounded-md overflow-hidden">
          <div className="px-3 py-2 bg-orange-50 border-b border-orange-200 flex items-center gap-2">
            <span className="text-[10px] font-mono font-bold text-orange-800">8</span>
            <h4 className="text-xs font-bold text-orange-900 flex-1">Terms — 2 editable sub-cells for invoicing</h4>
          </div>
          <div className="p-2 space-y-2">
            <TitleBodyCard step="8a" testId="inv-payment-terms" titleValue={drafts.inv_payment_terms_title} bodyValue={drafts.inv_payment_terms_body}
              titleFieldKey="inv_payment_terms_title" bodyFieldKey="inv_payment_terms_body" onFieldFocus={onFieldFocus}
              onCopyFrom={() => setCopyFromField('inv_payment_terms_pair')}
              bodyLibraryOptions={bodyLibraryOptions('invoice_payment_terms_body')}
              titleDefault="Payment Terms:" bodyDefault="Full Payment due within 7 days of invoicing unless prior arrangement."
              onTitleChange={v => setField('inv_payment_terms_title', v)} onBodyChange={v => setField('inv_payment_terms_body', v)}
              onUndoTitle={() => undoField('inv_payment_terms_title')} onUndoBody={() => undoField('inv_payment_terms_body')}
              titleUndoAvail={(undoStacks.inv_payment_terms_title || []).length > 0} bodyUndoAvail={(undoStacks.inv_payment_terms_body || []).length > 0}
              titleDirty={dirty.inv_payment_terms_title} titleSavedAt={savedAt.inv_payment_terms_title}
              bodyDirty={dirty.inv_payment_terms_body} bodySavedAt={savedAt.inv_payment_terms_body}
              hint="Uses {payment_due_days} token to inject the appraisal's payment window." />
            <TitleBodyCard step="8b" testId="inv-payment-amount" titleValue={drafts.inv_payment_amount_title} bodyValue={drafts.inv_payment_amount_body}
              titleFieldKey="inv_payment_amount_title" bodyFieldKey="inv_payment_amount_body" onFieldFocus={onFieldFocus}
              onCopyFrom={() => setCopyFromField('inv_payment_amount_pair')}
              bodyLibraryOptions={bodyLibraryOptions('invoice_payment_amount_body')}
              titleDefault="Payment Amount:" bodyDefault="Please deposit funds into the following account, referencing the Invoice number above: Westpac Bank 03 0314 0349045 00."
              onTitleChange={v => setField('inv_payment_amount_title', v)} onBodyChange={v => setField('inv_payment_amount_body', v)}
              onUndoTitle={() => undoField('inv_payment_amount_title')} onUndoBody={() => undoField('inv_payment_amount_body')}
              titleUndoAvail={(undoStacks.inv_payment_amount_title || []).length > 0} bodyUndoAvail={(undoStacks.inv_payment_amount_body || []).length > 0}
              titleDirty={dirty.inv_payment_amount_title} titleSavedAt={savedAt.inv_payment_amount_title}
              bodyDirty={dirty.inv_payment_amount_body} bodySavedAt={savedAt.inv_payment_amount_body}
              hint="Bank account + payment reference sentence. Uses {invoice_no}." />
          </div>
        </div>
        {/* 9️⃣ Representative — Invoice */}
        <TitleBodyCard step="9" testId="inv-representative"
          titleFieldKey="inv_representative_title" bodyFieldKey="inv_representative_body" onFieldFocus={onFieldFocus}
          onCopyFrom={() => setCopyFromField('inv_representative_pair')}
          bodyLibraryOptions={bodyLibraryOptions('invoice_representative_body')}
          titleValue={drafts.inv_representative_title} bodyValue={drafts.inv_representative_body}
          titleDefault="Representative:"
          bodyDefault="{representative_name}\nAfter Hours Phone: {after_hours_phone}"
          onTitleChange={v => setField('inv_representative_title', v)} onBodyChange={v => setField('inv_representative_body', v)}
          onUndoTitle={() => undoField('inv_representative_title')} onUndoBody={() => undoField('inv_representative_body')}
          titleUndoAvail={(undoStacks.inv_representative_title || []).length > 0} bodyUndoAvail={(undoStacks.inv_representative_body || []).length > 0}
          titleDirty={dirty.inv_representative_title} titleSavedAt={savedAt.inv_representative_title}
          bodyDirty={dirty.inv_representative_body} bodySavedAt={savedAt.inv_representative_body}
          hint="No acceptance/signature box on the invoice — work is already done." />
        </>) : (<>
        {/* 2️⃣ Thank-you Line — EDITABLE per quote_type (Tier 1) */}
        <TitleBodyCard
          step="2"
          testId="quotation-thanks"
          titleFieldKey="qt_thanks_title" bodyFieldKey="qt_thanks_body" onFieldFocus={onFieldFocus}
          onCopyFrom={() => setCopyFromField('qt_thanks_pair')}
          bodyLibraryOptions={bodyLibraryOptions('thanks_body')}
          titleValue={drafts.qt_thanks_title}
          bodyValue={drafts.qt_thanks_body}
          titleDefault={'Thank you'}
          bodyDefault={`Thank you for the opportunity of providing this quotation for the remedial repair of your roof at: ${address || '{property_address}'}`}
          onTitleChange={v => setField('qt_thanks_title', v)}
          onBodyChange={v => setField('qt_thanks_body', v)}
          onUndoTitle={() => undoField('qt_thanks_title')}
          onUndoBody={() => undoField('qt_thanks_body')}
          titleUndoAvail={(undoStacks.qt_thanks_title || []).length > 0}
          bodyUndoAvail={(undoStacks.qt_thanks_body || []).length > 0}
          titleDirty={dirty.qt_thanks_title} titleSavedAt={savedAt.qt_thanks_title}
          bodyDirty={dirty.qt_thanks_body} bodySavedAt={savedAt.qt_thanks_body}
          hint={`Per quote_type override. Empty = PDF prints the hardcoded default.`}
        />
        {/* 3️⃣ */}
        <ReadOnlyCard step="3" title="Quotation Type" hint="Set on the Appraisal form. Also the quote_type the Studio is currently editing.">
          <div className="text-[12px] text-slate-800 font-semibold">{selectedQtRef || '—'}</div>
        </ReadOnlyCard>
        {/* 4️⃣ Includes — editable */}
        <EditCard
          step="4" title='"Our Quotation Includes the Following:"'
          hint={`quote_types.description for "${selectedQtRef}". One bullet per line.`}
          testId="quotation-includes"
          fieldKey="qt_description" onFieldFocus={onFieldFocus}
          value={drafts.qt_description}
          onChange={v => setField('qt_description', v)}
          onUndo={() => undoField('qt_description')}
          undoAvailable={(undoStacks.qt_description || []).length > 0}
          onCopyFrom={() => setCopyFromField('qt_description')}
          dirty={dirty.qt_description} savedAt={savedAt.qt_description}
          rows={6}
        />
        {/* 5️⃣ */}
        <ReadOnlyCard step="5" title="Price Block" hint="Auto-computed from Appraisal → Estimates/Markup." />
        {/* v10.29 Sprint 4c-follow (2026-02) · Auto-Bullet Rules editor.
            Layer 2 (per-quote-type allowlist) editor + live preview.
            Uses the checkbox pattern rather than free-text templates —
            change component wording once via /api/bullet-rules and every
            quote_type re-renders with the new phrasing. */}
        <BulletV2Panel api={api} engine={drafts.qt_bullet_engine} groupsAllow={drafts.qt_bullet_v2_groups} setField={setField} />
        <div className="border border-emerald-300 rounded-md overflow-hidden" data-testid="quotation-bullet-rules-editor">
          <div className="px-3 py-2 bg-emerald-50 border-b border-emerald-200 flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-mono font-bold text-emerald-800">6</span>
            <h4 className="text-xs font-bold text-emerald-900 flex-1">"Repairs Detailed as Follows:" · OLD component allowlist + extra lines (extra lines also print with the new bullets)</h4>
            <button
              type="button"
              onClick={async () => {
                if (!quoteType?.service_option) return;
                try {
                  const { data } = await api.get(`/bullet-rules/quote-type-defaults/${encodeURIComponent(quoteType.service_option)}`);
                  setField('qt_bullet_include', Array.isArray(data?.bullet_include) ? data.bullet_include : []);
                  setField('qt_bullet_extra_lines', Array.isArray(data?.bullet_extra_lines) ? data.bullet_extra_lines : []);
                } catch (e) {
                  setError(e?.response?.data?.detail || e.message);
                }
              }}
              data-testid="quotation-bullet-reset-defaults"
              className="text-[11px] px-2 py-0.5 rounded-full border border-emerald-400 bg-white text-emerald-700 hover:bg-emerald-100 transition-colors"
              title={`Reset to canonical ${quoteType?.service_option || 'service'} cascade defaults`}
            >
              ↺ Reset to {quoteType?.service_option || 'service'} defaults
            </button>
          </div>
          <div className="p-3 space-y-3">
            <p className="text-[11px] text-slate-600">
              Tick the components you want to auto-bullet on the PDF for this quote type. Each rule's phrasing + unit is managed centrally in <strong>/admin/bullet-rules</strong> — edit it once and every quote type using that rule updates automatically.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5" data-testid="quotation-bullet-checkboxes">
              {bulletRules.map(rule => {
                const primaryCT = (rule.component_types || [])[0] || '';
                const includes = drafts.qt_bullet_include || [];
                const isChecked = (rule.component_types || []).some(ct => includes.includes(ct));
                return (
                  <label
                    key={rule._id}
                    className={`flex items-start gap-2 px-2 py-1.5 rounded border text-[11px] cursor-pointer transition-colors ${isChecked ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-slate-200 hover:bg-slate-50'}`}
                    data-testid={`quotation-bullet-check-${rule.key}`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => {
                        const current = new Set(drafts.qt_bullet_include || []);
                        if (e.target.checked) {
                          for (const ct of (rule.component_types || [])) current.add(ct);
                        } else {
                          for (const ct of (rule.component_types || [])) current.delete(ct);
                        }
                        setField('qt_bullet_include', Array.from(current));
                      }}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <BulletWordingInput api={api} rule={rule} variant={bulletVariant(quoteType)} onSaved={loadBulletRules} />
                      <div className="text-[10px] text-slate-500 font-mono-num truncate">
                        {rule.render_mode === 'itemised'
                          ? <>· one bullet per line (name + qty) <span className="text-emerald-700">(itemised, no $)</span></>
                          : rule.render_mode === 'present_only'
                          ? <>· each service name <span className="text-emerald-700">(present-only, no qty)</span></>
                          : <>· {variantLabel(rule, bulletVariant(quoteType))}   {`{${rule.token}}`} {rule.unit_suffix}</>}
                      </div>
                      <div className="text-[10px] text-slate-400 truncate" data-testid={`quotation-bullet-source-${rule.key}`}>
                        Pulls ALL lines of: {(rule.component_types || []).join(' + ')} (every work type, one total)
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>

            {/* Extra bullets — verbatim appends */}
            <div className="border-t border-slate-200 pt-3">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Extra bullet lines (verbatim)</div>
                <button
                  type="button"
                  onClick={() => setField('qt_bullet_extra_lines', [...(drafts.qt_bullet_extra_lines || []), ''])}
                  data-testid="quotation-bullet-add-extra"
                  className="text-[10px] px-2 py-0.5 rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                >+ Add extra</button>
              </div>
              {(drafts.qt_bullet_extra_lines || []).length === 0 ? (
                <div className="text-[11px] text-slate-400 italic">No extras. Add generic bullets that don't map to a component (e.g. "General site tidy on completion").</div>
              ) : (
                <div className="space-y-1">
                  {(drafts.qt_bullet_extra_lines || []).map((line, idx) => (
                    <div key={idx} className="flex items-center gap-1.5" data-testid={`quotation-bullet-extra-row-${idx}`}>
                      <span className="text-slate-400 text-[11px]">·</span>
                      <input
                        type="text"
                        value={line}
                        onChange={(e) => {
                          const next = [...(drafts.qt_bullet_extra_lines || [])];
                          next[idx] = e.target.value;
                          setField('qt_bullet_extra_lines', next);
                        }}
                        placeholder="Extra bullet text…"
                        className="flex-1 text-[11px] px-2 py-1 border border-slate-300 rounded"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const next = (drafts.qt_bullet_extra_lines || []).filter((_, i) => i !== idx);
                          setField('qt_bullet_extra_lines', next);
                        }}
                        data-testid={`quotation-bullet-extra-remove-${idx}`}
                        className="text-[10px] text-rose-600 px-1 hover:bg-rose-50 rounded"
                        title="Remove extra bullet"
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 6️⃣ Bullet Summary — Iter317 · Batch Q · Auto-populated from component_lines */}
        <EditCard
          step="6a" title='"Repairs Detailed as Follows:" · Auto-Bullet Summary'
          hint={`Bullet template for "${selectedQtRef}". Uses tokens like {ridge_qty_m} · {barge_qty_m} · {valley_qty_m} · {tile_count} · {aquaseal_count} that auto-resolve from the appraisal's component_lines at PDF render. Leave blank to fall back to the free-text field below.`}
          testId="quotation-bullet-summary"
          fieldKey="qt_bullet_summary" onFieldFocus={onFieldFocus}
          value={drafts.qt_bullet_summary}
          onChange={v => setField('qt_bullet_summary', v)}
          onUndo={() => undoField('qt_bullet_summary')}
          undoAvailable={(undoStacks.qt_bullet_summary || []).length > 0}
          onCopyFrom={() => setCopyFromField('qt_bullet_summary')}
          dirty={dirty.qt_bullet_summary} savedAt={savedAt.qt_bullet_summary}
          rows={7}
          placeholder={"· Bed and Point Ridging   {ridge_qty_m} meters\n· Repoint Barges          {barge_qty_m} meters\n· Replace Tiles           {tile_count} Tiles\n· Replace Valleys         {valley_qty_m} meters\n· Penetration flashings   {aquaseal_count} Lead based Aquaseals"}
        />
        {/* 6️⃣ Repairs — editable (free-text appendix; renders as "Additional Notes:" below the auto-bullets when both set) */}
        <EditCard
          step="6b" title='"Repairs Detailed as Follows:" · Free-Text (Additional Notes when auto-bullets set)'
          hint="Same field as the Roof Appraisal tab's Appraisal Cover Narrative. Edits here save to the appraisal."
          testId="quotation-repairs"
          fieldKey="appraisal_condition" onFieldFocus={onFieldFocus}
          value={drafts.appraisal_condition}
          onChange={v => setField('appraisal_condition', v)}
          onUndo={() => undoField('appraisal_condition')}
          undoAvailable={(undoStacks.appraisal_condition || []).length > 0}
          dirty={dirty.appraisal_condition} savedAt={savedAt.appraisal_condition}
          rows={6}
        />
        {/* 7️⃣ Notes — editable */}
        <EditCard
          step="7" title='"Notes:"'
          hint={`quote_types.details for "${selectedQtRef}". One note per line.`}
          testId="quotation-notes"
          fieldKey="qt_details" onFieldFocus={onFieldFocus}
          value={drafts.qt_details}
          onChange={v => setField('qt_details', v)}
          onUndo={() => undoField('qt_details')}
          undoAvailable={(undoStacks.qt_details || []).length > 0}
          onCopyFrom={() => setCopyFromField('qt_details')}
          dirty={dirty.qt_details} savedAt={savedAt.qt_details}
          rows={5}
        />
        {/* 8️⃣ TERMS — 5 editable sub-cells (title + body per quote_type) */}
        <div className="border border-emerald-300 rounded-md overflow-hidden">
          <div className="px-3 py-2 bg-emerald-50 border-b border-emerald-200 flex items-center gap-2">
            <span className="text-[10px] font-mono font-bold text-emerald-800">8</span>
            <h4 className="text-xs font-bold text-emerald-900 flex-1">Terms — 5 editable sub-cells (each has title + body per quote_type)</h4>
          </div>
          <div className="p-2 space-y-2">
            <TitleBodyCard step="8a" testId="qt-payment" titleValue={drafts.qt_payment_title} bodyValue={drafts.qt_payment_body}
              titleFieldKey="qt_payment_title" bodyFieldKey="qt_payment_body" onFieldFocus={onFieldFocus}
              onCopyFrom={() => setCopyFromField('qt_payment_pair')}
              bodyLibraryOptions={bodyLibraryOptions('payment_body')}
              titleDefault="Payment Terms:" bodyDefault="Payable on completion. Direct credit to Westpac Bank 03 0314 0349045 001."
              onTitleChange={v => setField('qt_payment_title', v)} onBodyChange={v => setField('qt_payment_body', v)}
              onUndoTitle={() => undoField('qt_payment_title')} onUndoBody={() => undoField('qt_payment_body')}
              titleUndoAvail={(undoStacks.qt_payment_title || []).length > 0} bodyUndoAvail={(undoStacks.qt_payment_body || []).length > 0}
              titleDirty={dirty.qt_payment_title} titleSavedAt={savedAt.qt_payment_title}
              bodyDirty={dirty.qt_payment_body} bodySavedAt={savedAt.qt_payment_body}
              hint="Per quote_type. Bank name + account number are hardcoded — will be tokenised in Tier 2." />
            <TitleBodyCard step="8b" testId="qt-deposit" titleValue={drafts.qt_deposit_title} bodyValue={drafts.qt_deposit_body}
              titleFieldKey="qt_deposit_title" bodyFieldKey="qt_deposit_body" onFieldFocus={onFieldFocus}
              onCopyFrom={() => setCopyFromField('qt_deposit_pair')}
              bodyLibraryOptions={bodyLibraryOptions('deposit_body')}
              titleDefault="Deposit Amount:" bodyDefault="30% of base price ($XXX) is required prior to commencement."
              onTitleChange={v => setField('qt_deposit_title', v)} onBodyChange={v => setField('qt_deposit_body', v)}
              onUndoTitle={() => undoField('qt_deposit_title')} onUndoBody={() => undoField('qt_deposit_body')}
              titleUndoAvail={(undoStacks.qt_deposit_title || []).length > 0} bodyUndoAvail={(undoStacks.qt_deposit_body || []).length > 0}
              titleDirty={dirty.qt_deposit_title} titleSavedAt={savedAt.qt_deposit_title}
              bodyDirty={dirty.qt_deposit_body} bodySavedAt={savedAt.qt_deposit_body}
              hint="Per quote_type. `{deposit_pct}` and `{deposit_amount}` tokens land in Tier 2." />
            <div className="flex flex-wrap items-center gap-2 pl-3 text-xs text-slate-600" data-testid="qt-deposit-rule-row">
              <span className="font-semibold">Deposit = greater of</span>
              <input data-testid="qt-deposit-standard-pct-input" type="number" min="0" max="100"
                value={drafts.default_deposit_pct ?? ''} onChange={e => setField('default_deposit_pct', e.target.value)}
                placeholder="20" className="w-16 px-2 py-1 border border-slate-300 rounded text-xs" />
              <span>% of quote, or supplier buy cost +</span>
              <input data-testid="qt-deposit-buy-uplift-input" type="number" min="0" max="200"
                value={drafts.buy_uplift_pct ?? ''} onChange={e => setField('buy_uplift_pct', e.target.value)}
                placeholder="25" className="w-16 px-2 py-1 border border-slate-300 rounded text-xs" />
              <span>% (incl GST, rounded to $10). Blank = 20% / 25%.</span>
              {['none', 'fixed_pct'].includes((quoteType?.deposit_rule || '').toLowerCase()) && (
                <span className="text-amber-700 font-semibold" data-testid="qt-deposit-rule-inactive">
                  Not used — this quote type's deposit rule is "{quoteType.deposit_rule}".
                </span>
              )}
            </div>
            <TitleBodyCard step="8c" testId="qt-commencement" titleValue={drafts.qt_commencement_title} bodyValue={drafts.qt_commencement_body}
              titleFieldKey="qt_commencement_title" bodyFieldKey="qt_commencement_body" onFieldFocus={onFieldFocus}
              onCopyFrom={() => setCopyFromField('qt_commencement_pair')}
              bodyLibraryOptions={bodyLibraryOptions('commencement_body')}
              titleDefault="Commencement:" bodyDefault="6-8 weeks from acceptance."
              onTitleChange={v => setField('qt_commencement_title', v)} onBodyChange={v => setField('qt_commencement_body', v)}
              onUndoTitle={() => undoField('qt_commencement_title')} onUndoBody={() => undoField('qt_commencement_body')}
              titleUndoAvail={(undoStacks.qt_commencement_title || []).length > 0} bodyUndoAvail={(undoStacks.qt_commencement_body || []).length > 0}
              titleDirty={dirty.qt_commencement_title} titleSavedAt={savedAt.qt_commencement_title}
              bodyDirty={dirty.qt_commencement_body} bodySavedAt={savedAt.qt_commencement_body}
              hint="Per quote_type — expected timeframes vary by job type." />
            <div className="flex items-center gap-2 pl-3 text-xs text-slate-600" data-testid="qt-commencement-window-row">
              <label htmlFor="qt-commencement-window" className="font-semibold">{'{commencement_window}'} for this quote type:</label>
              <input id="qt-commencement-window" data-testid="qt-commencement-window-input"
                value={drafts.quote_commencement || ''} onChange={e => setField('quote_commencement', e.target.value)}
                placeholder="e.g. 4–6" className="w-28 px-2 py-1 border border-slate-300 rounded text-xs" />
              <span className="text-slate-400">An appraisal-level value overrides this.</span>
            </div>
            <TitleBodyCard step="8d" testId="qt-general" titleValue={drafts.qt_general_title} bodyValue={drafts.qt_general_body}
              titleFieldKey="qt_general_title" bodyFieldKey="qt_general_body" onFieldFocus={onFieldFocus}
              onCopyFrom={() => setCopyFromField('qt_general_pair')}
              bodyLibraryOptions={bodyLibraryOptions('general_body')}
              titleDefault="General Condition:" bodyDefault="Quotation subject to weather, site access, and unforeseen conditions. Any deviation from the scope will be quoted separately before proceeding."
              onTitleChange={v => setField('qt_general_title', v)} onBodyChange={v => setField('qt_general_body', v)}
              onUndoTitle={() => undoField('qt_general_title')} onUndoBody={() => undoField('qt_general_body')}
              titleUndoAvail={(undoStacks.qt_general_title || []).length > 0} bodyUndoAvail={(undoStacks.qt_general_body || []).length > 0}
              titleDirty={dirty.qt_general_title} titleSavedAt={savedAt.qt_general_title}
              bodyDirty={dirty.qt_general_body} bodySavedAt={savedAt.qt_general_body}
              hint="Per quote_type. Editable." />
            <TitleBodyCard step="8e" testId="qt-validity" titleValue={drafts.qt_validity_title} bodyValue={drafts.qt_validity_body}
              titleFieldKey="qt_validity_title" bodyFieldKey="qt_validity_body" onFieldFocus={onFieldFocus}
              onCopyFrom={() => setCopyFromField('qt_validity_pair')}
              bodyLibraryOptions={bodyLibraryOptions('validity_body')}
              titleDefault="Quotation Valid:" bodyDefault="45 days from the date of this quotation."
              onTitleChange={v => setField('qt_validity_title', v)} onBodyChange={v => setField('qt_validity_body', v)}
              onUndoTitle={() => undoField('qt_validity_title')} onUndoBody={() => undoField('qt_validity_body')}
              titleUndoAvail={(undoStacks.qt_validity_title || []).length > 0} bodyUndoAvail={(undoStacks.qt_validity_body || []).length > 0}
              titleDirty={dirty.qt_validity_title} titleSavedAt={savedAt.qt_validity_title}
              bodyDirty={dirty.qt_validity_body} bodySavedAt={savedAt.qt_validity_body}
              hint="Per quote_type. `{validity_days}` token lands in Tier 2." />
          </div>
        </div>
        {/* 9️⃣ Representative + Acceptance — EDITABLE per quote_type */}
        <TitleBodyCard step="9" testId="qt-representative"
          titleFieldKey="qt_representative_title" bodyFieldKey="qt_representative_body" onFieldFocus={onFieldFocus}
          onCopyFrom={() => setCopyFromField('qt_representative_pair')}
          bodyLibraryOptions={bodyLibraryOptions('representative_body')}
          titleValue={drafts.qt_representative_title} bodyValue={drafts.qt_representative_body}
          titleDefault="Representative:" bodyDefault={`${appraisal?.appraisal_by || '{rep_name}'}\nAcceptance signature: ____________________ Date: __________`}
          onTitleChange={v => setField('qt_representative_title', v)} onBodyChange={v => setField('qt_representative_body', v)}
          onUndoTitle={() => undoField('qt_representative_title')} onUndoBody={() => undoField('qt_representative_body')}
          titleUndoAvail={(undoStacks.qt_representative_title || []).length > 0} bodyUndoAvail={(undoStacks.qt_representative_body || []).length > 0}
          titleDirty={dirty.qt_representative_title} titleSavedAt={savedAt.qt_representative_title}
          bodyDirty={dirty.qt_representative_body} bodySavedAt={savedAt.qt_representative_body}
          hint="Per quote_type. Rep name comes from appraisal — will be tokenised in Tier 2." />
        </>)}
      </div>

      {/* Copy-From modal */}
      {copyFromField && (() => {
        const pair = TITLE_BODY_COPY_MAP[copyFromField];
        if (pair) {
          // Pair mode — cells 2, 8a-8e, 9 (title + body together, both variants).
          return (
            <CopyFromModal
              list={quoteTypes}
              currentRef={selectedQtRef}
              fieldLabel={pair.label}
              fieldKey={pair.bodyDb}
              secondaryFieldKey={pair.titleDb}
              onPick={(sourceQt) => doCopyFrom(sourceQt, copyFromField)}
              onClose={() => setCopyFromField(null)}
            />
          );
        }
        // Single-field paths (cells 4 & 7) — variant-aware label + DB key.
        const singleMap = {
          qt_description:  { label: 'Our Quotation Includes',      db: 'description'         },
          qt_details:      { label: 'Quotation Notes',             db: 'details'             },
          inv_description: { label: 'Our Invoice Includes',        db: 'invoice_description' },
          inv_details:     { label: 'Invoice Notes',               db: 'invoice_details'     },
        };
        const cfg = singleMap[copyFromField] || singleMap.qt_details;
        return (
          <CopyFromModal
            list={quoteTypes}
            currentRef={selectedQtRef}
            fieldLabel={cfg.label}
            fieldKey={cfg.db}
            onPick={(sourceQt) => doCopyFrom(sourceQt, copyFromField)}
            onClose={() => setCopyFromField(null)}
          />
        );
      })()}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────
function ReadOnlyCard({ step, title, hint, children }) {
  return (
    <div className="border border-slate-200 rounded-md overflow-hidden bg-slate-50/50">
      <div className="px-3 py-2 bg-slate-100/60 border-b border-slate-200 flex items-center gap-2">
        <span className="text-[10px] font-mono text-slate-500 font-bold">{step}</span>
        <h4 className="text-xs font-bold text-slate-700 flex-1">{title}</h4>
        <Lock size={10} className="text-slate-400" />
      </div>
      {(hint || children) && (
        <div className="px-3 py-2">
          {hint && <p className="text-[10px] italic text-slate-500 mb-1">{hint}</p>}
          {children}
        </div>
      )}
    </div>
  );
}

function EditCard({ step, title, hint, testId, fieldKey, onFieldFocus, value, onChange, onUndo, undoAvailable, onCopyFrom, dirty, savedAt, rows = 4, placeholder }) {
  const showSaved = savedAt && Date.now() - savedAt < 3500;
  return (
    <div className="border border-emerald-300 rounded-md overflow-hidden bg-white">
      <div className="px-3 py-2 bg-emerald-50 border-b border-emerald-200 flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-mono font-bold text-emerald-800">{step}</span>
        <h4 className="text-xs font-bold text-emerald-900 flex-1 min-w-0 truncate">{title}</h4>
        {dirty && <span className="text-[10px] text-amber-700 flex items-center gap-1"><Loader2 className="animate-spin" size={10} /> Saving…</span>}
        {showSaved && !dirty && <span className="text-[10px] text-emerald-700 flex items-center gap-1"><Save size={10} /> Saved</span>}
        {onUndo && (
          <button
            type="button" onClick={onUndo} disabled={!undoAvailable}
            data-testid={`${testId}-undo`}
            className="p-1 text-slate-500 hover:text-emerald-800 hover:bg-emerald-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
            title="Undo last change (Ctrl+Z)"
          ><Undo2 size={12} /></button>
        )}
        {onCopyFrom && (
          <button
            type="button" onClick={onCopyFrom}
            data-testid={`${testId}-copy-from`}
            className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 border border-emerald-300 rounded hover:bg-emerald-100"
            title="Copy from another quote_type"
          >
            <ClipboardCopy size={10} /> Copy from
          </button>
        )}
      </div>
      <div className="px-3 py-2">
        {hint && <p className="text-[10px] italic text-slate-500 mb-1.5">{hint}</p>}
        <textarea
          data-testid={testId}
          data-field-key={fieldKey}
          onFocus={onFieldFocus}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          onKeyDown={e => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'z' && onUndo && undoAvailable) {
              e.preventDefault();
              onUndo();
            }
          }}
          rows={rows}
          placeholder={placeholder || ''}
          className="w-full px-2.5 py-1.5 border border-emerald-300 bg-white rounded text-[12px] leading-snug font-mono resize-y focus:outline-none focus:border-emerald-600"
        />
      </div>
    </div>
  );
}

function TitleBodyCard({
  step, testId, hint,
  titleValue, bodyValue, titleDefault, bodyDefault,
  onTitleChange, onBodyChange,
  onUndoTitle, onUndoBody, titleUndoAvail, bodyUndoAvail,
  titleDirty, titleSavedAt, bodyDirty, bodySavedAt,
  titleFieldKey, bodyFieldKey, onFieldFocus,
  onCopyFrom, bodyLibraryOptions,
}) {
  // Q3(c) — flag "using default" when a field is empty so admins can
  // audit which quote_types still lack overrides. Warning shown on the
  // header, not in the placeholder, so it's visible even when a user
  // types then deletes.
  const missingTitle = !titleValue?.trim();
  const missingBody = !bodyValue?.trim();
  const anyMissing = missingTitle || missingBody;
  return (
    <div className={`border rounded-md overflow-hidden ${anyMissing ? 'border-amber-300 bg-amber-50/30' : 'border-emerald-300 bg-white'}`}>
      <div className={`px-3 py-2 border-b flex items-center gap-2 flex-wrap ${anyMissing ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
        <span className={`text-[10px] font-mono font-bold ${anyMissing ? 'text-amber-800' : 'text-emerald-800'}`}>{step}</span>
        {anyMissing && (
          <span className="text-[10px] font-bold uppercase tracking-wider text-amber-800 bg-amber-100 border border-amber-300 rounded px-1.5" title="One or both fields are empty — the PDF will fall back to a default. Fill both to override.">
            ⚠ using default
          </span>
        )}
        {hint && <span className="text-[10px] italic text-slate-500 flex-1 min-w-0 truncate">{hint}</span>}
        {onCopyFrom && (
          <button
            type="button" onClick={onCopyFrom}
            data-testid={`${testId}-copy-from`}
            className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 border border-emerald-300 rounded hover:bg-emerald-100 ml-auto"
            title="Copy title + body together from another quote_type"
          >
            <ClipboardCopy size={10} /> Copy from
          </button>
        )}
      </div>
      <div className="px-3 py-2 space-y-1.5">
        {/* Title (single line) */}
        <TitleBodySubField
          kind="title" testId={`${testId}-title`}
          fieldKey={titleFieldKey} onFieldFocus={onFieldFocus}
          value={titleValue} defaultVal={titleDefault}
          onChange={onTitleChange} onUndo={onUndoTitle} undoAvail={titleUndoAvail}
          dirty={titleDirty} savedAt={titleSavedAt}
          placeholder={titleDefault}
        />
        {/* Body (multi-line) */}
        <TitleBodySubField
          kind="body" testId={`${testId}-body`}
          fieldKey={bodyFieldKey} onFieldFocus={onFieldFocus}
          value={bodyValue} defaultVal={bodyDefault}
          onChange={onBodyChange} onUndo={onUndoBody} undoAvail={bodyUndoAvail}
          dirty={bodyDirty} savedAt={bodySavedAt}
          placeholder={bodyDefault}
          libraryOptions={bodyLibraryOptions}
        />
      </div>
    </div>
  );
}

function TitleBodySubField({ kind, testId, value, defaultVal, onChange, onUndo, undoAvail, dirty, savedAt, placeholder, fieldKey, onFieldFocus, libraryOptions }) {
  const showSaved = savedAt && Date.now() - savedAt < 3500;
  const missing = !value?.trim();
  const hasLibrary = kind === 'body' && Array.isArray(libraryOptions) && libraryOptions.length > 0;
  return (
    <div className="flex items-start gap-2">
      <span className="text-[9px] uppercase tracking-wider font-bold text-slate-500 pt-1 shrink-0 w-10">{kind}</span>
      <div className="flex-1 min-w-0">
        {kind === 'title' ? (
          <input
            type="text" value={value || ''} onChange={e => onChange(e.target.value)}
            onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'z' && undoAvail) { e.preventDefault(); onUndo(); } }}
            data-testid={testId} placeholder={placeholder}
            data-field-key={fieldKey} onFocus={onFieldFocus}
            className={`w-full px-2 py-1 border rounded text-[12px] focus:outline-none ${missing ? 'border-amber-300 bg-white text-slate-400 italic' : 'border-emerald-300 focus:border-emerald-600'}`}
          />
        ) : (
          <>
            {/* iter299u — Body-only "Load from library" dropdown.
                Options are the distinct non-empty body values on the
                same field across all OTHER quote_types. Native <select>
                keeps the UI compact; picking an option replaces the
                current value via the standard setField() path (so
                undo + auto-save both fire). */}
            {hasLibrary && (
              <select
                data-testid={`${testId}-library`}
                defaultValue=""
                onChange={e => { const v = e.target.value; if (v) onChange(v); e.target.value = ''; }}
                className="w-full mb-1 px-2 py-0.5 border border-emerald-200 bg-emerald-50/50 rounded text-[10px] focus:outline-none focus:border-emerald-600 cursor-pointer"
                title="Pick a body script that's already used on another quote_type"
              >
                <option value="">▼ Load body from library ({libraryOptions.length})</option>
                {libraryOptions.map((opt, i) => (
                  <option key={i} value={opt.value}>
                    {opt.qtRef}: {opt.value.replace(/\s+/g, ' ').slice(0, 90)}{opt.value.length > 90 ? '…' : ''}
                  </option>
                ))}
              </select>
            )}
            <textarea
              value={value || ''} onChange={e => onChange(e.target.value)}
              onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'z' && undoAvail) { e.preventDefault(); onUndo(); } }}
              data-testid={testId} placeholder={placeholder} rows={2}
              data-field-key={fieldKey} onFocus={onFieldFocus}
              className={`w-full px-2 py-1 border rounded text-[12px] font-mono resize-y focus:outline-none ${missing ? 'border-amber-300 bg-white text-slate-400 italic' : 'border-emerald-300 focus:border-emerald-600'}`}
            />
          </>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0 pt-1">
        {dirty && <span className="text-[10px] text-amber-700 flex items-center gap-0.5"><Loader2 className="animate-spin" size={10} /></span>}
        {showSaved && !dirty && <span className="text-[10px] text-emerald-700 flex items-center gap-0.5"><Save size={10} /></span>}
        {onUndo && (
          <button type="button" onClick={onUndo} disabled={!undoAvail}
            data-testid={`${testId}-undo`}
            className="p-0.5 text-slate-500 hover:text-emerald-800 hover:bg-emerald-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
            title="Undo (Ctrl+Z)"
          ><Undo2 size={11} /></button>
        )}
      </div>
    </div>
  );
}

function TermRow({ label, hint, readOnly, testId, value, onChange, onUndo, undoAvailable, dirty, savedAt, type = 'text', placeholder }) {  const showSaved = savedAt && Date.now() - savedAt < 3500;
  return (
    <div className={`px-3 py-2 ${readOnly ? 'bg-slate-50/50' : 'bg-white'}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[10px] font-bold uppercase tracking-wider ${readOnly ? 'text-slate-500' : 'text-emerald-800'}`}>{label}</span>
        {readOnly && <Lock size={10} className="text-slate-400" />}
        {dirty && <span className="text-[10px] text-amber-700 flex items-center gap-1"><Loader2 className="animate-spin" size={10} /> Saving…</span>}
        {showSaved && !dirty && <span className="text-[10px] text-emerald-700 flex items-center gap-1"><Save size={10} /> Saved</span>}
        {!readOnly && onUndo && (
          <button
            type="button" onClick={onUndo} disabled={!undoAvailable}
            data-testid={`${testId}-undo`}
            className="ml-auto p-0.5 text-slate-500 hover:text-emerald-800 hover:bg-emerald-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
            title="Undo (Ctrl+Z)"
          ><Undo2 size={11} /></button>
        )}
      </div>
      {hint && <p className="text-[10px] italic text-slate-500 mt-0.5">{hint}</p>}
      {!readOnly && (
        <input
          type={type}
          value={value === undefined || value === null ? '' : value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={e => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'z' && onUndo && undoAvailable) {
              e.preventDefault();
              onUndo();
            }
          }}
          data-testid={testId}
          placeholder={placeholder || ''}
          className="mt-1 w-full px-2 py-1 border border-emerald-300 rounded text-[12px] focus:outline-none focus:border-emerald-600"
        />
      )}
    </div>
  );
}

function QuoteTypePicker({ list, onPick, currentRef }) {
  const [q, setQ] = useState('');
  const grouped = list.reduce((acc, qt) => {
    const cat = qt.quote_category || 'Uncategorised';
    (acc[cat] = acc[cat] || []).push(qt);
    return acc;
  }, {});
  const cats = Object.keys(grouped).sort();
  const filtered = (qs) => qs.filter(qt => !q || (qt.ref || '').toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="quote-type-picker">
      <h3 className="text-sm font-bold text-emerald-900 mb-1">Pick a Quotation Type to preview & edit</h3>
      <p className="text-[11px] text-slate-600 mb-3">The Quotation Letter has no roof-type variants — each variant here is one of the {list.length} Quote Types on your appraisals.</p>
      <div className="mb-3 flex items-center gap-2">
        <input
          type="text"
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Filter by name…"
          className="flex-1 px-2.5 py-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:border-emerald-600"
          data-testid="qt-picker-search"
          autoFocus
        />
        <a
          href="/quote-types-manage"
          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold border border-slate-300 text-slate-700 rounded hover:bg-slate-50"
        >
          <Settings size={11} /> Manage
        </a>
      </div>
      <div className="max-h-[520px] overflow-y-auto space-y-3">
        {cats.map(cat => {
          const rows = filtered(grouped[cat]);
          if (rows.length === 0) return null;
          return (
            <div key={cat}>
              <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500 mb-1">{cat} ({rows.length})</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {rows.map(qt => (
                  <button
                    key={qt.ref}
                    type="button"
                    onClick={() => onPick(qt.ref)}
                    data-testid={`qt-pick-${qt.ref.replace(/\s+/g, '-')}`}
                    className={`text-left px-2.5 py-1.5 rounded border text-[11px] transition-colors ${
                      qt.ref === currentRef
                        ? 'bg-emerald-100 border-emerald-500 text-emerald-900 font-bold'
                        : 'bg-white border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 text-slate-800'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {qt.ref === currentRef && <Check size={11} className="text-emerald-700" />}
                      <span className="truncate">{qt.ref}</span>
                    </div>
                    {qt.roof_type && <span className="text-[9px] text-slate-500">{qt.roof_type}</span>}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CopyFromModal({ list, currentRef, fieldLabel, fieldKey, secondaryFieldKey, onPick, onClose }) {
  const [q, setQ] = useState('');
  // In pair mode either the title OR body being non-empty on the source
  // qualifies — copying still overwrites both draft fields (blanks come
  // through as blanks, which is the correct semantic).
  const others = list.filter(qt => qt.ref !== currentRef && (
    !!qt[fieldKey] || (secondaryFieldKey && !!qt[secondaryFieldKey])
  ));
  const filtered = others.filter(qt => !q || (qt.ref || '').toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h4 className="text-sm font-bold text-slate-900">Copy {fieldLabel} from another Quote Type</h4>
            <p className="text-[11px] text-slate-500 mt-0.5">Click any card below — its {fieldLabel} {secondaryFieldKey ? 'title + body' : 'text'} will replace your current one (undo with ↺ or Ctrl+Z).</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="copy-from-close"><X size={16} /></button>
        </div>
        <div className="p-3">
          <input
            type="text" value={q} onChange={e => setQ(e.target.value)} autoFocus
            placeholder="Filter…" data-testid="copy-from-search"
            className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:border-emerald-600"
          />
        </div>
        <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-1.5">
          {filtered.length === 0 && <p className="p-4 text-xs text-slate-500 italic text-center">No other quote_types have {fieldLabel} filled in.</p>}
          {filtered.map(qt => (
            <button
              key={qt.ref}
              type="button"
              onClick={() => onPick(qt)}
              data-testid={`copy-from-${qt.ref.replace(/\s+/g, '-')}`}
              className="w-full text-left px-3 py-2 rounded border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 transition-colors"
            >
              <div className="flex items-center gap-2 text-[11px] font-bold text-slate-900 mb-1">
                <ClipboardCopy size={10} className="text-emerald-600" /> {qt.ref}
                {qt.quote_category && <span className="text-[9px] font-normal text-slate-500 ml-auto">{qt.quote_category}</span>}
              </div>
              {secondaryFieldKey && (
                <p className="text-[10px] font-semibold text-emerald-800 leading-snug mb-0.5">
                  {qt[secondaryFieldKey] || <span className="italic text-slate-400">(empty title)</span>}
                </p>
              )}
              <p className="text-[10px] text-slate-700 leading-snug whitespace-pre-wrap line-clamp-3">{qt[fieldKey] || <span className="italic text-slate-400">(empty body)</span>}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}


// iter299t — Grouped token chip palette. Mirrors the exact chip styling
// used by ScriptAuditPage.js `INSERT_FIELD_GROUPS` on the Detailed Multi
// / Detailed 1 studio pages so the two editors feel consistent.
function TokenPalette({ onInsert }) {
  return (
    <div
      data-testid="quotation-token-palette"
      className="border border-slate-200 rounded-md bg-white px-3 py-2 space-y-1"
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Insert Field</span>
        <span className="text-[10px] italic text-slate-400">— click a text field first, then a chip to splice at the caret · unknown/empty tokens print literally so you can spot & fix them.</span>
      </div>
      {QUOTATION_TOKEN_GROUPS.map((grp) => (
        <div key={grp.label} className="flex flex-wrap items-center gap-1">
          <span className="text-[10px] font-semibold text-slate-500 mr-1 uppercase tracking-wider min-w-[92px]">{grp.label}:</span>
          {grp.tokens.map((t) => (
            <button
              key={t.key}
              type="button"
              data-testid={`quotation-insert-token-${t.key}`}
              onClick={() => onInsert(t.key)}
              title={`Insert {${t.key}} at cursor · resolves to the appraisal's "${t.label}" at PDF render`}
              className="px-1.5 py-0.5 text-[10px] font-mono border border-slate-200 rounded bg-slate-50 hover:bg-[#316536] hover:text-white hover:border-[#316536] text-slate-700 transition-colors"
            >
              {`{${t.key}}`}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}
